import { Option, TableRow } from '@code/innovance-react-ui-kit';
import React, { createContext, FC, useContext, useReducer } from 'react';

import { UploadDocumentResponse } from 'src/front/api/generated';

import {
  customerSearchDefaultValues,
  SearchFormData,
} from 'src/front/components/CustomerSearchForm';
import { UploadDocumentFormData } from 'src/front/components/document/UploadDocumentModal';

import { CardReissueTableRow } from 'src/front/pages/applications/card-reissue/CardReissueCardSearch';

enum ACTIONS {
  SEARCH_CUSTOMER,
  SEARCH_CARD,
  SET_REASONS,
  SET_DOCUMENTS,
  SET_SINGLE_DOCUMENT,
  CLEAR_CARD_SEARCH,
  CLEAR,
}

type Action =
  | {
      type: ACTIONS.SEARCH_CUSTOMER;
      payload: {
        customerSearchFormValues: SearchFormData;
        customerSearchValues: CustomerSearchValues;
      };
    }
  | { type: ACTIONS.CLEAR }
  | { type: ACTIONS.CLEAR_CARD_SEARCH }
  | {
      type: ACTIONS.SET_DOCUMENTS;
      payload: { documents?: UploadDocumentFormData[] };
    }
  | {
      type: ACTIONS.SET_SINGLE_DOCUMENT;
      payload: { documentFormData?: UploadDocumentResponse };
    }
  | {
      type: ACTIONS.SEARCH_CARD;
      payload: { cardSearchValues: CardSearchValues };
    }
  | {
      type: ACTIONS.SET_REASONS;
      payload: { cardReissueValues: CardReissueValues };
    };

interface CustomerSearchValues {
  customerId: string;
  customerNumber: string;
  clientName: string;
  selectedCustomer: TableRow;
  customerPageNumber: number;
}

interface CardSearchValues {
  pos: Option | null;
  branch: Option | null;
  cardListType: Option | null;
  cardPageNumber: number;
  selectedCard?: CardReissueTableRow;
}

interface CardReissueValues {
  cardStatus: Option | null;
  cardSubStatus: Option | null;
  cardReasonForReissue: Option | null;
  embossedName: string;
  embossedNameOther: string;
  isNoNameCard: boolean;
  noNameCardId: string;
  lastFourDigits: string;
}

interface CardReissueState {
  customerSearchFormValues: SearchFormData;
  customerSearchValues: CustomerSearchValues;
  cardSearchValues: CardSearchValues;
  documentFormData?: UploadDocumentResponse;
  documents?: UploadDocumentFormData[];
  cardReissueValues: CardReissueValues;
}

const initialValues = {
  customerSearchFormValues: customerSearchDefaultValues,
  customerSearchValues: {
    customerId: '',
    customerNumber: '',
    clientName: '',
    selectedCustomer: {
      id: '',
      columns: [],
    },
    customerPageNumber: 1,
  },
  cardSearchValues: {
    pos: null,
    branch: null,
    cardListType: null,
    cardPageNumber: 1,
  },
  cardReissueValues: {
    cardStatus: null,
    cardSubStatus: null,
    cardReasonForReissue: null,
    embossedName: '',
    embossedNameOther: '',
    isNoNameCard: false,
    noNameCardId: '',
    lastFourDigits: '',
  },
};

type CardReissueDispatch = (action: Action) => void;

const CardReissueStateContext = createContext<CardReissueState | undefined>(undefined);
const CardReissueDispatchContext = createContext<CardReissueDispatch | undefined>(undefined);

const cardReissueReducer = (state: CardReissueState, action: Action): CardReissueState => {
  switch (action.type) {
    case ACTIONS.SEARCH_CUSTOMER:
    case ACTIONS.SEARCH_CARD:
    case ACTIONS.SET_REASONS:
    case ACTIONS.SET_DOCUMENTS:
    case ACTIONS.SET_SINGLE_DOCUMENT:
      return {
        ...state,
        ...action.payload,
      };
    case ACTIONS.CLEAR_CARD_SEARCH:
      return {
        ...initialValues,
        customerSearchFormValues: state.customerSearchFormValues,
        customerSearchValues: state.customerSearchValues,
      };
    case ACTIONS.CLEAR:
      return initialValues;
    default:
      return state;
  }
};

const CardReissueProvider: FC = ({ children }) => {
  const [state, dispatch] = useReducer(cardReissueReducer, initialValues);
  return (
    <CardReissueStateContext.Provider value={state}>
      <CardReissueDispatchContext.Provider value={dispatch}>
        {children}
      </CardReissueDispatchContext.Provider>
    </CardReissueStateContext.Provider>
  );
};

export const useCardReissueState = (): CardReissueState => {
  const context = useContext(CardReissueStateContext);
  if (context === undefined) {
    throw new Error('useCardReissueState must be used within a CardReissueProvider');
  }

  return context;
};

type SearchInput = Pick<CardReissueState, 'customerSearchFormValues' | 'customerSearchValues'>;
type Documents = Pick<CardReissueState, 'documents'>;
type Document = Pick<CardReissueState, 'documentFormData'>;
type CardSearch = Pick<CardReissueState, 'cardSearchValues'>;
type Reasons = Pick<CardReissueState, 'cardReissueValues'>;

export const useCustomerSearchDispatch = (): ((payload: SearchInput) => void) => {
  const dispatch = useContext(CardReissueDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useCustomerSearchDispatch must be used within a CardReissueProvider');
  }

  return (payload: SearchInput) => dispatch({ type: ACTIONS.SEARCH_CUSTOMER, payload });
};

export const useCardSearchDispatch = (): ((payload: CardSearch) => void) => {
  const dispatch = useContext(CardReissueDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useCardSearchDispatch must be used within a CardReissueProvider');
  }

  return (payload: CardSearch) => dispatch({ type: ACTIONS.SEARCH_CARD, payload });
};

export const useSetReasonsDispatch = (): ((payload: Reasons) => void) => {
  const dispatch = useContext(CardReissueDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useSetReasons must be used within a CardReissueProvider');
  }

  return (payload: Reasons) => dispatch({ type: ACTIONS.SET_REASONS, payload });
};

export const useSetSingleDocumentDispatch = (): ((payload: Document) => void) => {
  const dispatch = useContext(CardReissueDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useSetSingleDocument must be used within a CardReissueProvider');
  }

  return (payload: Document) => dispatch({ type: ACTIONS.SET_SINGLE_DOCUMENT, payload });
};

export const useSetDocumentsDispatch = (): ((payload: Documents) => void) => {
  const dispatch = useContext(CardReissueDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useSetDocuments must be used within a CardReissueProvider');
  }

  return (payload: Documents) => dispatch({ type: ACTIONS.SET_DOCUMENTS, payload });
};

export const useClearCardSearchDispatch = (): (() => void) => {
  const dispatch = useContext(CardReissueDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useClearCardSearchDispatch must be used within a CardReissueProvider');
  }

  return () => dispatch({ type: ACTIONS.CLEAR_CARD_SEARCH });
};

export const useClearDispatch = (): (() => void) => {
  const dispatch = useContext(CardReissueDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useClearDispatch must be used within a CardReissueProvider');
  }

  return () => dispatch({ type: ACTIONS.CLEAR });
};

export default CardReissueProvider;
